import type { PushJobItemStatus, PushJobMode, PushJobSummary, PushLlmInput } from "@tg/shared";
import { httpBaseFromWsUrl } from "./auth-api.js";

// HTTP-клиент ИИ-пропуша (раунд 46). Ошибки сервера отдаются с {code,
// message} — пробрасываем message, чтобы модалка показала человекочитаемое
// («уже идёт пропуш», «не заданы промпт/модель»).

export interface PushSettingsPublic {
  model: string;
  contextMessages: number;
  delayMinSec: number;
  delayMaxSec: number;
  maxChatsPerRun: number;
  maxReplyChars: number;
  promptLength: number;
  prompt?: string;
}

export interface PushSettingsFull {
  model: string;
  prompt: string;
  contextMessages: number;
  delayMinSec: number;
  delayMaxSec: number;
  maxChatsPerRun: number;
  maxReplyChars: number;
}

// Лимиты формы «Пуш скриптами» (сервер — SCRIPT_PUSH_LIMITS в push-store).
export interface ScriptPushLimits {
  maxScripts: number;
  maxScriptChars: number;
  minDelaySec: number;
  maxDelaySec: number;
  defaultDelayMinSec: number;
  defaultDelayMaxSec: number;
}

export interface StartPushInput {
  accountId: string;
  listId: string;
  listTitle: string;
  deleteFolderOnFinish: boolean;
  mode?: PushJobMode;
  // Только для mode=scripts.
  scripts?: string[];
  delayMinSec?: number;
  delayMaxSec?: number;
  maxChatsPerRun?: number;
}

export interface PushJobItemView {
  chatId: string;
  position: number;
  userName?: string | null;
  status: PushJobItemStatus;
  reason: string | null;
  generatedText?: string | null;
  // Только менеджерам: что ушло в модель (см. PushItemsReport).
  llmInput?: PushLlmInput;
  llmSystemPrompt?: string;
  sentMessageId: string | null;
  updatedAt: string;
}

const request = async <TResponse>(url: string, init?: RequestInit): Promise<TResponse> => {
  const response = await fetch(url, { credentials: "same-origin", ...init });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: string; message?: string }
      | null;
    throw new Error(body?.message ?? body?.error ?? `Request failed with status ${response.status}`);
  }
  return (await response.json()) as TResponse;
};

const json = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body)
});

export const pushApi = {
  getSettings(
    accountId: string,
    wsUrl: string
  ): Promise<{
    canStart: boolean;
    configured: boolean;
    integrationConfigured: boolean;
    settings: PushSettingsPublic;
    scriptLimits: ScriptPushLimits;
    // Незавершённые пуши аккаунта: в работе и в очереди.
    activeJobs: PushJobSummary[];
  }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/push/settings?accountId=${encodeURIComponent(accountId)}`);
  },
  startJob(
    input: StartPushInput,
    wsUrl: string
  ): Promise<{ job: PushJobSummary }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/push/jobs`, json(input));
  },
  listJobs(
    accountId: string,
    wsUrl: string,
    limit = 20,
    options?: { activeOnly?: boolean }
  ): Promise<{ jobs: PushJobSummary[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(
      `${base}/api/push/jobs?accountId=${encodeURIComponent(accountId)}&limit=${limit}${
        options?.activeOnly ? "&active=1" : ""
      }`
    );
  },
  // Галочка «Удалить папку после завершения» у уже поставленной папки.
  updateJob(
    jobId: string,
    patch: { deleteFolderOnFinish: boolean },
    wsUrl: string
  ): Promise<{ job: PushJobSummary }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/push/jobs/${encodeURIComponent(jobId)}`, {
      ...json(patch),
      method: "PATCH"
    });
  },
  getJob(
    jobId: string,
    wsUrl: string
  ): Promise<{ job: PushJobSummary; report: string; items: PushJobItemView[]; scripts?: string[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/push/jobs/${encodeURIComponent(jobId)}`);
  },
  // Для пуша «в очереди» — убрать из очереди, для идущего — остановить.
  cancelJob(jobId: string, wsUrl: string): Promise<{ job: PushJobSummary }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/push/jobs/${encodeURIComponent(jobId)}/cancel`, json({}));
  },
  adminListSettings(
    wsUrl: string
  ): Promise<{ accounts: Record<string, PushSettingsFull>; defaults: PushSettingsFull }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/admin/push/settings`);
  },
  adminSetSettings(
    input: { accountId: string; settings?: PushSettingsFull; clear?: boolean },
    wsUrl: string
  ): Promise<{ settings: PushSettingsFull | null }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/admin/push/settings`, { ...json(input), method: "PUT" });
  },
  adminListJobs(wsUrl: string, limit = 50): Promise<{ jobs: PushJobSummary[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/admin/push/jobs?limit=${limit}`);
  },
  listModels(wsUrl: string): Promise<Array<{ id: string; name?: string }>> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok?: boolean; models?: Array<{ id: string; name?: string }> }>(
      `${base}/api/admin/integrations/llm-models`,
      json({ kind: "translator" })
    ).then((payload) => payload.models ?? []);
  }
};
